import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import { api } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Loader2,
  Heart,
  HeartCrack,
  Award,
  RefreshCw,
  Sparkles,
  Users,
  Trophy
} from 'lucide-react';
import { loadStudentPhotos, createPhotoDataUrl, hasPhoto } from '../lib/photoUtils';

interface ClassItem {
  Klasse: string;
  anzahl?: number;
}

interface StudentRecord {
  sophomorixUnid: string;
  Name: string;
  Vorname: string;
  Klasse: string;
  Foto?: string;
}

interface QuizStudent extends StudentRecord {
  fullName: string;
  correctCount: number;
  attempts: number;
}

interface HighscoreEntry {
  username: string;
  score: number;
  correctAnswers?: number;
  livesRemaining?: number;
  updatedAt?: string;
  createdAt?: string;
}

interface HighscorePayload {
  score: number;
  correctAnswers: number;
  livesRemaining: number;
  totalStudents: number;
  classes: string[];
  durationSeconds: number;
}

type GamePhase = 'setup' | 'loading' | 'playing' | 'finished';

type AnswerState = {
  selectedId: string | null;
  correctId: string | null;
  isCorrect: boolean | null;
};

interface Question {
  studentId: string;
  photo: string;
  options: Array<{ id: string; fullName: string }>;
  correctId: string;
}

const REQUIRED_CORRECT_PER_STUDENT = 3;
const INITIAL_LIVES = 3;
const BONUS_LIFE_THRESHOLD = 15;

const getHighscoreTimestamp = (entry: HighscoreEntry) => entry.updatedAt || entry.createdAt || '';

export default function NamenLernenQuiz() {
  const { user } = useAuth();
  const [selectedClasses, setSelectedClasses] = useState<string[]>([]);
  const [gamePhase, setGamePhase] = useState<GamePhase>('setup');
  const [quizStudents, setQuizStudents] = useState<QuizStudent[]>([]);
  const quizStudentsRef = useRef<QuizStudent[]>([]);
  const [question, setQuestion] = useState<Question | null>(null);
  const [answerState, setAnswerState] = useState<AnswerState>({ selectedId: null, correctId: null, isCorrect: null });
  const [showCorrectName, setShowCorrectName] = useState(false);
  const [lives, setLives] = useState(INITIAL_LIVES);
  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [studentPhotos, setStudentPhotos] = useState<Record<string, string | null>>({});
  const [bonusLivesAwarded, setBonusLivesAwarded] = useState(0);
  const [summary, setSummary] = useState<{ reason: 'completed' | 'outOfLives'; durationSeconds: number } | null>(null);
  const [round, setRound] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isSubmittingScore, setIsSubmittingScore] = useState(false);
  const startTimestampRef = useRef<number | null>(null);
  const questionTimeoutRef = useRef<number | null>(null);
  const gamePhaseRef = useRef<GamePhase>('setup');
  const classesInitializedRef = useRef(false);

  // Klassen laden
  const { data: classesResponse, isLoading: classesLoading } = useQuery(
    ['namen-lernen-classes'],
    async () => {
      const response = await api.get('/students/classes');
      return response.data?.data || [];
    },
    {
      onError: () => {
        toast.error('Klassen konnten nicht geladen werden');
      }
    }
  );

  const classes: ClassItem[] = useMemo(() => {
    if (!classesResponse) return [];
    return classesResponse
      .map((item: any) => ({ Klasse: item.Klasse || item.klasse || item.name, anzahl: item.anzahl || item.Anzahl }))
      .filter((item: ClassItem) => !!item.Klasse)
      .sort((a: ClassItem, b: ClassItem) => a.Klasse.localeCompare(b.Klasse, 'de-DE'));
  }, [classesResponse]);

  useEffect(() => {
    if (!classesInitializedRef.current && classes.length > 0) {
      setSelectedClasses(classes.map(item => item.Klasse));
      classesInitializedRef.current = true;
    }
  }, [classes]);

  // Highscore laden
  const { data: highscoreResponse, isLoading: highscoreLoading, refetch: refetchHighscores } = useQuery(
    ['namen-lernen-highscores'],
    async () => {
      const response = await api.get('/namen-lernen/quiz/highscores');
      return response.data?.data || response.data;
    },
    {
      refetchOnWindowFocus: false,
      onError: () => {
        toast.error('Highscores konnten nicht geladen werden');
      }
    }
  );

  const highscores: HighscoreEntry[] = useMemo(() => {
    if (!highscoreResponse) return [];
    if (Array.isArray(highscoreResponse)) {
      return highscoreResponse;
    }
    if (Array.isArray(highscoreResponse?.highscores)) {
      return highscoreResponse.highscores;
    }
    return [];
  }, [highscoreResponse]);

  const userBestScore: HighscoreEntry | null = useMemo(() => {
    if (!highscoreResponse) return null;
    if (highscoreResponse?.userBest) {
      return highscoreResponse.userBest;
    }
    const username = user?.username?.toLowerCase();
    if (!username) return null;
    const sorted = highscores
      .filter(entry => entry.username?.toLowerCase() === username)
      .sort((a, b) => (b.score ?? 0) - (a.score ?? 0));
    return sorted[0] ?? null;
  }, [highscoreResponse, highscores, user?.username]);

  const totalRequiredCorrect = useMemo(() => quizStudents.length * REQUIRED_CORRECT_PER_STUDENT, [quizStudents.length]);
  const remainingCorrectNeeded = Math.max(0, totalRequiredCorrect - correctAnswers);

  const resetGameState = useCallback(() => {
    setQuizStudents([]);
    quizStudentsRef.current = [];
    setQuestion(null);
    setAnswerState({ selectedId: null, correctId: null, isCorrect: null });
    setShowCorrectName(false);
    setLives(INITIAL_LIVES);
    setScore(0);
    setCorrectAnswers(0);
    setBonusLivesAwarded(0);
    setSummary(null);
    setRound(0);
    setError(null);
    startTimestampRef.current = null;
    if (questionTimeoutRef.current) {
      window.clearTimeout(questionTimeoutRef.current);
      questionTimeoutRef.current = null;
    }
  }, []);

useEffect(() => () => {
  if (questionTimeoutRef.current) {
    window.clearTimeout(questionTimeoutRef.current);
  }
}, []);

  useEffect(() => {
    gamePhaseRef.current = gamePhase;
  }, [gamePhase]);

  const buildQuestionWithPhotos = useCallback((students: QuizStudent[], photos: Record<string, string | null>, previousStudentId?: string): Question | null => {
    const available = students.filter(student => student.correctCount < REQUIRED_CORRECT_PER_STUDENT && hasPhoto(photos[student.sophomorixUnid]));
    
    if (available.length === 0) {
      return null;
    }

    const pool = available.length > 1 && previousStudentId
      ? available.filter(student => student.sophomorixUnid !== previousStudentId) || available
      : available;

    const selected = pool[Math.floor(Math.random() * pool.length)];

    // Verwende nur Schüler mit Fotos als Distraktoren, aber schließe den ausgewählten Schüler aus
    const distractorsSource = students.filter(student => 
      student.sophomorixUnid !== selected.sophomorixUnid && 
      hasPhoto(photos[student.sophomorixUnid])
    );
    
    const shuffled = [...distractorsSource].sort(() => Math.random() - 0.5);
    
    // Erstelle Optionen: 1 korrekte Antwort + 3 Distraktoren
    const options = [
      { id: selected.sophomorixUnid, fullName: `${selected.Vorname} ${selected.Name}` },
      ...shuffled.slice(0, 3).map(student => ({
        id: student.sophomorixUnid,
        fullName: `${student.Vorname} ${student.Name}`
      }))
    ];

    // Fülle mit Platzhaltern auf, falls nicht genug Distraktoren vorhanden sind
    while (options.length < 4) {
      options.push({ id: `${selected.sophomorixUnid}-placeholder-${options.length}`, fullName: '—' });
    }

    const shuffledOptions = [...options].sort(() => Math.random() - 0.5);

    return {
      studentId: selected.sophomorixUnid,
      photo: photos[selected.sophomorixUnid]!,
      options: shuffledOptions,
      correctId: selected.sophomorixUnid,
    };
  }, []);

  const buildQuestion = useCallback((students: QuizStudent[], previousStudentId?: string): Question | null => {
    return buildQuestionWithPhotos(students, studentPhotos, previousStudentId);
  }, [buildQuestionWithPhotos, studentPhotos]);

  const finishGame = useCallback((reason: 'completed' | 'outOfLives') => {
    if (questionTimeoutRef.current) {
      window.clearTimeout(questionTimeoutRef.current);
      questionTimeoutRef.current = null;
    }

    const endTimestamp = Date.now();
    const durationSeconds = startTimestampRef.current ? Math.round((endTimestamp - startTimestampRef.current) / 1000) : 0;
    setSummary({ reason, durationSeconds });
    setGamePhase('finished');

    setIsSubmittingScore(true);
    submitHighscoreMutation.mutate(
      {
        score,
        correctAnswers,
        livesRemaining: lives,
        totalStudents: quizStudents.length,
        classes: selectedClasses,
        durationSeconds,
      },
      {
        onSettled: () => {
          setIsSubmittingScore(false);
          refetchHighscores();
        }
      }
    );
  }, [correctAnswers, lives, quizStudents.length, refetchHighscores, score, selectedClasses]);

  const prepareNextQuestion = useCallback((previousStudentId?: string) => {
    const source = quizStudentsRef.current;
    const nextQuestion = buildQuestion(source, previousStudentId);
    setAnswerState({ selectedId: null, correctId: null, isCorrect: null });
    setShowCorrectName(false);
    if (nextQuestion) {
      setRound(r => r + 1);
      setQuestion(nextQuestion);
    } else {
      // Keine Fragen mehr verfügbar - Spiel beenden
      finishGame('completed');
    }
  }, [buildQuestion, finishGame]);

  const submitHighscoreMutation = useMutation(
    (payload: HighscorePayload) => api.post('/namen-lernen/quiz/highscore', payload),
    {
      onSuccess: () => {
        toast.success('Neuer Highscore gespeichert!');
      },
      onError: () => {
        toast.error('Highscore konnte nicht gespeichert werden');
      }
    }
  );

  const handleAnswer = useCallback((optionId: string) => {
    if (!question || answerState.selectedId || gamePhase !== 'playing') {
      return;
    }

    const isCorrect = optionId === question.correctId;
    setAnswerState({ selectedId: optionId, correctId: question.correctId, isCorrect });
    setShowCorrectName(!isCorrect);

    setQuizStudents(prev => {
      const updated = prev.map(student => {
        if (student.sophomorixUnid === question.correctId) {
          return {
            ...student,
            attempts: student.attempts + 1,
            correctCount: student.correctCount + (isCorrect ? 1 : 0),
          };
        }
        return student;
      });
      quizStudentsRef.current = updated;
      return updated;
    });

    let endedDueToLives = false;

    if (isCorrect) {
      setScore(prev => prev + 1);
      setCorrectAnswers(prev => {
        const nextTotal = prev + 1;
        setBonusLivesAwarded(prevBonus => {
          const thresholdReached = Math.floor(nextTotal / BONUS_LIFE_THRESHOLD);
          if (thresholdReached > prevBonus) {
            setLives(l => l + 1);
            toast.success('Bonusleben erhalten!');
            return thresholdReached;
          }
          return prevBonus;
        });
        return nextTotal;
      });
      setTimeout(() => {
        toast.success('Richtig!');
      }, 80);
    } else {
      setLives(prev => {
        const nextLives = prev - 1;
        toast.error('Leider falsch');
        if (nextLives <= 0) {
          endedDueToLives = true;
          questionTimeoutRef.current = window.setTimeout(() => finishGame('outOfLives'), 900);
        }
        return nextLives;
      });
    }

    if (questionTimeoutRef.current) {
      window.clearTimeout(questionTimeoutRef.current);
    }

    if (questionTimeoutRef.current) {
      window.clearTimeout(questionTimeoutRef.current);
    }

    questionTimeoutRef.current = window.setTimeout(() => {
      if (endedDueToLives || gamePhaseRef.current !== 'playing') {
        return;
      }
      setAnswerState({ selectedId: null, correctId: null, isCorrect: null });
      setShowCorrectName(false);
      prepareNextQuestion(question.studentId);
    }, isCorrect ? 1000 : 3000);
  }, [answerState.selectedId, finishGame, gamePhase, prepareNextQuestion, question]);

  useEffect(() => {
    if (gamePhase === 'playing' && quizStudents.length > 0) {
      // Prüfe, ob alle Schüler 3x richtig beantwortet wurden
      const allStudentsCompleted = quizStudents.every(student => student.correctCount >= REQUIRED_CORRECT_PER_STUDENT);
      if (allStudentsCompleted) {
        finishGame('completed');
      }
    }
  }, [quizStudents, finishGame, gamePhase]);

  const fetchStudentsForGame = useCallback(async () => {
    setGamePhase('loading');
    setError(null);

    try {
      const response = await api.get('/students', {
        params: {
          includePhotos: 'true'
        }
      });

      const rawStudents: StudentRecord[] = response.data?.data || [];
      const filteredByClass = selectedClasses.length > 0
        ? rawStudents.filter(student => selectedClasses.includes(student.Klasse))
        : rawStudents;

      const uniqueStudents = Array.from(
        new Map(filteredByClass.map(student => [student.sophomorixUnid, student])).values()
      );

      if (uniqueStudents.length < 4) {
        setError('Für die ausgewählten Klassen stehen nicht genügend Schüler zur Verfügung.');
        setGamePhase('setup');
        toast.error('Nicht genug Schüler verfügbar.');
        return;
      }

      // Load student photos from DATA_PATH in batches to avoid the 100 photo limit
      const batchSize = 50; // Load photos in batches of 50
      const allPhotos: Record<string, string | null> = {};
      
      for (let i = 0; i < uniqueStudents.length; i += batchSize) {
        const batch = uniqueStudents.slice(i, i + batchSize);
        const batchIds = batch.map(student => student.sophomorixUnid);
        const batchPhotos = await loadStudentPhotos(batchIds);
        Object.assign(allPhotos, batchPhotos);
      }
      
      setStudentPhotos(allPhotos);

      // Filter students that have photos
      const withPhotos = uniqueStudents.filter(student => hasPhoto(allPhotos[student.sophomorixUnid]));


      if (withPhotos.length < 4) {
        setError(`Für die ausgewählten Klassen stehen nicht genügend Schülerfotos zur Verfügung. Gefunden: ${withPhotos.length} von ${uniqueStudents.length} Schülern.`);
        setGamePhase('setup');
        toast.error(`Nicht genug Fotos verfügbar (${withPhotos.length}/${uniqueStudents.length}).`);
        return;
      }

      const shuffledStudents = [...withPhotos].sort(() => Math.random() - 0.5);

      const initialized = shuffledStudents.map(student => ({
        ...student,
        fullName: `${student.Vorname} ${student.Name}`,
        correctCount: 0,
        attempts: 0,
      }));

      quizStudentsRef.current = initialized;
      setQuizStudents(initialized);
      setStudentPhotos(allPhotos);
      startTimestampRef.current = Date.now();
      setGamePhase('playing');
      
      // Pass photos directly to buildQuestion instead of relying on state
      const firstQuestion = buildQuestionWithPhotos(initialized, allPhotos) ?? null;
      
      setQuestion(firstQuestion);
      setRound(firstQuestion ? 1 : 0);
    } catch (err) {
      console.error('Fehler beim Laden der Schüler:', err);
      setError('Schüler konnten nicht geladen werden.');
      setGamePhase('setup');
      toast.error('Schüler konnten nicht geladen werden');
    }
  }, [buildQuestion, selectedClasses]);

  const handleStartGame = useCallback(() => {
    resetGameState();
    fetchStudentsForGame();
  }, [fetchStudentsForGame, resetGameState]);

  const handleClassToggle = (klasse: string) => {
    setSelectedClasses(prev => 
      prev.includes(klasse) ? prev.filter(item => item !== klasse) : [...prev, klasse]
    );
  };

  const handleSelectAllClasses = () => {
    setSelectedClasses(classes.map(item => item.Klasse));
    classesInitializedRef.current = true;
  };

  const handleClearClasses = () => {
    setSelectedClasses([]);
    classesInitializedRef.current = true;
  };

  const canStart = selectedClasses.length > 0 && !classesLoading;

  return (
    <div className="max-w-6xl mx-auto px-6 py-8">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/namen-lernen" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900">
          <ArrowLeft className="w-5 h-5" />
          Zurück
        </Link>
        <h1 className="text-3xl font-bold text-gray-900">Namen lernen · Quiz</h1>
      </div>

      <div className="grid gap-8 lg:grid-cols-[2fr_1fr]">
        <div className="space-y-6">
          {gamePhase === 'setup' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">Klassen auswählen</h2>
                  <p className="text-gray-500 text-sm">Wählen Sie eine oder mehrere Klassen aus – oder lassen Sie alle ausgewählt, um das gesamte Namensrepertoire zu trainieren.</p>
                </div>
                <Users className="w-8 h-8 text-primary-500" />
              </div>

              {classesLoading ? (
                <div className="flex items-center gap-2 text-gray-500">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Klassen werden geladen…
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={handleSelectAllClasses}
                      className="px-3 py-1.5 text-sm rounded-full bg-primary-50 text-primary-700 hover:bg-primary-100 transition-colors"
                    >
                      Alle auswählen
                    </button>
                    <button
                      type="button"
                      onClick={handleClearClasses}
                      className="px-3 py-1.5 text-sm rounded-full bg-gray-100 text-gray-600 hover:bg-gray-200 transition-colors"
                    >
                      Auswahl leeren
                    </button>
                    {selectedClasses.length > 0 && (
                      <span className="px-3 py-1.5 text-sm rounded-full bg-green-100 text-green-700">
                        {selectedClasses.length} Klasse{selectedClasses.length === 1 ? '' : 'n'} ausgewählt
                      </span>
                    )}
                  </div>

                  <div className="grid gap-2 sm:grid-cols-2 md:grid-cols-3">
                    {classes.map(klasse => {
                      const isSelected = selectedClasses.includes(klasse.Klasse);
                      return (
                        <label
                          key={klasse.Klasse}
                          className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer transition-all ${
                            isSelected ? 'border-primary-500 bg-primary-50 text-primary-700' : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="rounded text-primary-600 focus:ring-primary-500"
                            checked={isSelected}
                            onChange={() => handleClassToggle(klasse.Klasse)}
                          />
                          <span className="font-medium">{klasse.Klasse}</span>
                          {klasse.anzahl && (
                            <span className="text-xs text-gray-500">({klasse.anzahl})</span>
                          )}
                        </label>
                      );
                    })}
                  </div>

                  {error && (
                    <div className="rounded-xl bg-red-50 text-red-700 px-4 py-3 text-sm">
                      {error}
                    </div>
                  )}

                  {!canStart && !error && (
                    <div className="rounded-xl bg-amber-50 text-amber-700 px-4 py-3 text-sm">
                      Bitte wählen Sie mindestens eine Klasse aus, um zu starten.
                    </div>
                  )}

                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={handleStartGame}
                      disabled={!canStart}
                      className={`w-full md:w-auto px-6 py-3 rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 ${
                        canStart
                          ? 'bg-primary-600 text-white hover:bg-primary-700'
                          : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      Spiel starten
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {gamePhase === 'loading' && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 flex flex-col items-center justify-center gap-4">
              <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
              <p className="text-gray-600">Spiel wird vorbereitet…</p>
            </div>
          )}

          {gamePhase === 'playing' && question && (
            <div className="space-y-6">
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 text-red-500 font-semibold">
                      {Array.from({ length: Math.max(lives, 0) }).map((_, index) => (
                        <Heart key={index} className="w-5 h-5 fill-current" />
                      ))}
                      {lives <= 0 && <HeartCrack className="w-5 h-5 text-gray-400" />}
                    </div>
                    <div className="text-gray-500 text-sm">
                      Leben: <span className="font-semibold text-gray-900">{Math.max(lives, 0)}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Punkte</div>
                    <div className="text-2xl font-bold text-gray-900">{score}</div>
                  </div>
                </div>

                <div className="w-full aspect-square bg-gray-100 rounded-2xl overflow-hidden flex items-center justify-center shadow-inner">
                  {question.photo ? (
                    <img
                      src={createPhotoDataUrl(question.photo)}
                      alt="Schülerfoto"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-gray-400 text-sm">Kein Foto verfügbar</div>
                  )}
                </div>

                <div className="grid gap-3 mt-6 md:grid-cols-2">
                  {question.options.map(option => {
                    const isSelected = answerState.selectedId === option.id;
                    const isCorrect = answerState.correctId === option.id;
                    const stateClass = answerState.selectedId
                      ? isCorrect
                        ? 'border-green-500 bg-green-50 text-green-800'
                        : isSelected
                          ? 'border-red-500 bg-red-50 text-red-700'
                          : 'border-gray-200'
                      : 'border-gray-200 hover:border-primary-300 hover:bg-primary-50';

                    return (
                      <button
                        key={option.id}
                        type="button"
                        onClick={() => handleAnswer(option.id)}
                        className={`px-4 py-3 rounded-xl border text-left font-medium transition-all ${stateClass}`}
                        disabled={!!answerState.selectedId}
                      >
                        {option.fullName}
                        {isCorrect && answerState.selectedId && (
                          <span className="ml-2 inline-flex items-center gap-1 text-xs text-green-700">
                            <Sparkles className="w-3 h-3" />Richtig
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                {showCorrectName && answerState.selectedId && answerState.isCorrect === false && (
                  <div className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800">
                    <span className="font-semibold">Richtiger Name:</span>{' '}
                    {question.options.find(option => option.id === answerState.correctId)?.fullName || 'Unbekannt'}
                  </div>
                )}

                <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-gray-500">
                  <div>
                    Runde <span className="font-semibold text-gray-900">{round}</span>
                  </div>
                  <div>
                    Fortschritt: <span className="font-semibold text-gray-900">{correctAnswers}</span> / {totalRequiredCorrect}
                  </div>
                  <div>
                    Noch <span className="font-semibold text-gray-900">{remainingCorrectNeeded}</span> richtige Antworten bis zum Sieg
                  </div>
                </div>
              </div>
            </div>
          )}

          {gamePhase === 'finished' && summary && (
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 space-y-6">
              <div className="flex items-center gap-3">
                <Trophy className="w-10 h-10 text-amber-500" />
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">{summary.reason === 'completed' ? 'Glückwunsch!' : 'Gut gekämpft!'}</h2>
                  <p className="text-gray-500">{summary.reason === 'completed'
                    ? 'Sie haben alle Namen sicher erkannt.'
                    : 'Die Leben sind aufgebraucht – versuchen Sie es gleich noch einmal!'}
                  </p>
                </div>
              </div>

              <dl className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                <div className="bg-primary-50 rounded-xl px-4 py-3">
                  <dt className="text-sm text-primary-600">Punkte</dt>
                  <dd className="text-2xl font-bold text-primary-700">{score}</dd>
                </div>
                <div className="bg-green-50 rounded-xl px-4 py-3">
                  <dt className="text-sm text-green-600">Richtige Antworten</dt>
                  <dd className="text-2xl font-bold text-green-700">{correctAnswers}</dd>
                </div>
                <div className="bg-amber-50 rounded-xl px-4 py-3">
                  <dt className="text-sm text-amber-600">Verbleibende Leben</dt>
                  <dd className="text-2xl font-bold text-amber-700">{Math.max(lives, 0)}</dd>
                </div>
                <div className="bg-blue-50 rounded-xl px-4 py-3">
                  <dt className="text-sm text-blue-600">Gespielte Klassen</dt>
                  <dd className="text-lg font-semibold text-blue-700">{
                    selectedClasses.length === classes.length
                      ? 'Alle'
                      : selectedClasses.length > 0
                        ? selectedClasses.join(', ')
                        : 'Keine'
                  }</dd>
                </div>
                <div className="bg-purple-50 rounded-xl px-4 py-3">
                  <dt className="text-sm text-purple-600">Trainierte Schüler</dt>
                  <dd className="text-lg font-semibold text-purple-700">{quizStudents.length}</dd>
                </div>
                <div className="bg-gray-100 rounded-xl px-4 py-3">
                  <dt className="text-sm text-gray-600">Spielzeit</dt>
                  <dd className="text-lg font-semibold text-gray-800">{summary.durationSeconds}s</dd>
                </div>
              </dl>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => {
                    resetGameState();
                    setGamePhase('setup');
                  }}
                  className="px-5 py-3 rounded-xl border border-gray-300 text-gray-700 hover:bg-gray-100 transition-colors flex items-center gap-2"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Zurück zur Auswahl
                </button>
                <button
                  type="button"
                  onClick={handleStartGame}
                  className="px-5 py-3 rounded-xl bg-primary-600 text-white hover:bg-primary-700 transition-colors flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Noch einmal spielen
                </button>
              </div>

              {isSubmittingScore && (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Highscore wird aktualisiert…
                </div>
              )}
            </div>
          )}
        </div>

        <aside className="space-y-6">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900">Highscores</h2>
              <Award className="w-6 h-6 text-amber-500" />
            </div>

            {highscoreLoading ? (
              <div className="flex items-center gap-2 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin" />
                Highscores werden geladen…
              </div>
            ) : highscores.length === 0 ? (
              <p className="text-gray-500 text-sm">Noch keine Einträge vorhanden. Spielen Sie eine Runde und setzen Sie den ersten Rekord!</p>
            ) : (
              <ul className="space-y-2">
                {highscores.slice(0, 10).map((entry, index) => {
                  const isCurrentUser = entry.username && user?.username && entry.username.toLowerCase() === user.username.toLowerCase();
                  return (
                    <li
                      key={`${entry.username}-${getHighscoreTimestamp(entry)}-${index}`}
                      className={`flex items-center justify-between px-4 py-3 rounded-xl border ${
                        isCurrentUser ? 'border-primary-400 bg-primary-50 text-primary-800' : 'border-gray-200'
                      }`}
                    >
                      <div>
                        <div className="text-sm font-semibold">{index + 1}. {entry.username || 'Unbekannt'}</div>
                        <div className="text-xs text-gray-500">
                          {getHighscoreTimestamp(entry) ? new Date(getHighscoreTimestamp(entry)!).toLocaleDateString('de-DE') : '—'}
                        </div>
                      </div>
                      <div className="text-lg font-bold">{entry.score ?? 0} Punkte</div>
                    </li>
                  );
                })}
              </ul>
            )}

            {userBestScore && (
              <div className="mt-4 rounded-xl bg-green-50 text-green-700 px-4 py-3 text-sm">
                <div className="font-semibold">Ihr persönlicher Rekord</div>
                <div className="flex items-center justify-between">
                  <span>{userBestScore.score ?? 0} Punkte</span>
                  {userBestScore.correctAnswers && (
                    <span>{userBestScore.correctAnswers} richtige Antworten</span>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 space-y-4 text-sm text-gray-600">
            <h3 className="text-lg font-semibold text-gray-900">Spielregeln</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Wählen Sie eine oder mehrere Klassen als Trainingsbasis.</li>
              <li>Für jede Runde erscheint ein Schülerfoto – klicken Sie auf den passenden Namen.</li>
              <li>Jede richtige Antwort bringt einen Punkt. Bei 15 richtigen Antworten erhalten Sie ein Bonus-Leben.</li>
              <li>Bei falscher Antwort verlieren Sie ein Leben. Mit drei Leben starten Sie ins Spiel.</li>
              <li>Das Spiel endet, wenn alle Fotos dreimal richtig erkannt wurden oder keine Leben mehr übrig sind.</li>
              <li>Ihre höchste Punktzahl wird im Highscore verewigt.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
